import { memo, useCallback, useState } from 'react';
import { cn } from '@divlab/divanui';

import Link from '@Navigation/Link';
import Img from '@UI/Img';
import useTranslation from '@Queries/useTranslation';
import styles from './ArticlesNavButton.module.css';

import type { NavArticleData } from '../../typings';
import type { FC, HTMLAttributes, ReactElement } from 'react';

export interface ArticlesNavButtonProps extends HTMLAttributes<HTMLDivElement> {
  icon: ReactElement;
  article?: NavArticleData;
  direction: 'prev' | 'next';
}

const ArticlesNavButton: FC<ArticlesNavButtonProps> = (props) => {
  const { icon, article, direction, ...restProps } = props;
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  return (
    <div
      {...restProps}
      className={cn(styles.button, {
        [styles.hovered]: hovered,
        [styles.disabled]: !article,
        [styles.directionNext]: direction === 'next',
        [styles.directionPrev]: direction === 'prev',
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {article ? (
        <Link to={article.link} className={styles.link}>
          {icon}
        </Link>
      ) : (
        <div className={styles.link}>{icon}</div>
      )}

      {article && (
        <div className={styles.popup}>
          <div className={styles.popupBody}>
            <Img src={article.image} className={styles.popupImage} />
            <div className={styles.popupText}>
              {article.rubric && <div className={styles.rubric}>{article.rubric}</div>}
              <div className={styles.popupTitle}>{article.title}</div>

              <Link to={article.link} className={styles.popupMore} underlined view='primary'>
                {t('read')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ArticlesNavButton);
